/**
 * Which URLs an assistant answer may turn into clickable links.
 * Framework-free (copied to next-mapit).
 *
 * Only `https:` on the default port, without credentials, to `mapit.sa` (and
 * its subdomains), `wa.me` or `api.whatsapp.com`. Everything else renders as
 * plain text.
 */

/** The registrable domain whose subdomains are all allowed. */
const MAPIT_DOMAIN = "mapit.sa";

/** Hosts allowed exactly (no subdomains). */
const EXACT_HOSTS = Object.freeze(["wa.me", "api.whatsapp.com"]);

const isAllowedHost = (hostname) =>
  hostname === MAPIT_DOMAIN ||
  hostname.endsWith(`.${MAPIT_DOMAIN}`) ||
  EXACT_HOSTS.includes(hostname);

/**
 * The normalized href to put on the link, or null when the URL isn't allowed.
 *
 * Render this value rather than the raw text, so the browser follows exactly
 * the URL that was checked (the WHATWG parser drops tabs/newlines, lowercases
 * the host, decodes `%2e` in hosts, …). Relative and protocol-relative URLs
 * are rejected; a trailing-dot host (`mapit.sa.`) is rejected too.
 *
 * @param {unknown} url
 * @returns {string | null}
 */
export function getAllowedHref(url) {
  if (typeof url !== "string" || !url) return null;

  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  if (parsed.protocol !== "https:") return null;
  // `https://mapit.sa@evil.com` puts "mapit.sa" in the username.
  if (parsed.username || parsed.password) return null;
  // The parser already blanks the default port (443).
  if (parsed.port) return null;
  if (!isAllowedHost(parsed.hostname)) return null;

  return parsed.href;
}

/**
 * @param {unknown} url
 * @returns {boolean}
 */
export const isAllowedUrl = (url) => getAllowedHref(url) !== null;
